import React, {
  CSSProperties,
  FC,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import {CheckboxProps, FormHelperText} from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import {UseFormReturn, Validate} from "react-hook-form";
import {FormContext} from "view/components/Form";
import {isEqual} from "lodash";
import clsx from "clsx";
import {FixedSizeList as List} from "react-window";
import useStyles from "./styles";

export type CheckboxGroupItem = {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
  children?: (parentSelected: boolean) => React.ReactNode;
};

export type CheckboxGroupProps = {
  includeAll?: boolean;
  /**
   * Let the "All" checkbox toggle DISABLED items too, turning it into a master
   * switch over a group whose members are locked on purpose (Copy Task's
   * settings: all-or-nothing, so the individual rows are read-only). Off by
   * default — a locked row normally means "All" must leave it alone.
   */
  allTogglesDisabledItems?: boolean;
  ignoreVirtualization?: boolean;
  includeAllLabel?: string;
  defaultValues?: string[];
  name: string;
  items: CheckboxGroupItem[];
  required?: boolean;
  maxSelection?: number;
  classes?: {
    item?: string;
    itemContainer?: string;
  };
  maxHeigth?: string;
  validation?: Validate<any, any>;
  renderAdditionalComponent?: (item: CheckboxGroupItem) => React.ReactNode;
  onEndReached?: () => void;
} & Pick<CheckboxProps, "onChange">;

const END_REACHED_OFFSET = 5;

const CheckboxGroup: FC<CheckboxGroupProps> = ({
  items,
  name,
  includeAll,
  allTogglesDisabledItems = false,
  defaultValues = [],
  ignoreVirtualization = false,
  validation,
  includeAllLabel,
  classes: classNames,
  renderAdditionalComponent,
  maxHeigth: maxHeightProp,
  required = true,
  onChange: propsOnChange,
  onEndReached,
}): ReactElement => {
  const classes = useStyles();
  const [selectedData, setSelectedData] = useState<string[]>(defaultValues);
  const form = useContext(FormContext) as UseFormReturn;
  const {setValue, register, trigger, watch} = form;
  const maxHeight = maxHeightProp || "500px";
  const formValue = watch(name);

  const allSelected =
    items.length > 0 && items.every(({value}) => selectedData.includes(value));

  useEffect(() => {
    const formFieldValue = formValue || [];
    if (!isEqual(selectedData, formFieldValue)) {
      setSelectedData(formFieldValue);
    }
  }, [formValue, selectedData]);

  const formLabelProps = (label: React.ReactNode) => ({
    classes: {
      root: classes.controlLabelRoot,
      label: classes.label,
    },
    label,
  });

  const checkboxProps = (isSelected: boolean) => ({
    checked: isSelected,
    classes: {
      root: classes.root,
      checked: classes.checked,
    },
    color: "primary" as const,
  });
  register(name, {
    required,
    validate: validation,
  });

  const onChange = (values: string[]) => {
    setSelectedData(values);

    trigger(name);
    setValue(name, values, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const checkboxGroupError = form.formState.errors[name];

  const renderAllCheckbox = () => (
    <Box className={clsx(classes.itemContainer, classNames?.item)}>
      <FormControlLabel
        control={
          <Checkbox
            name="all"
            onChange={
              propsOnChange ??
              (() => {
                // Union / subtract against the visible items only — never
                // wipe a selection the caller is currently hiding (search).
                // Disabled items are excluded from BOTH directions: their
                // individual checkbox can't be toggled, so "All" must not
                // toggle them either (e.g. locked conditional tasks) — unless
                // the caller made "All" the master switch over locked rows,
                // where excluding them leaves nothing to toggle and the
                // checkbox looks broken.
                const togglable = items
                  .filter((item) => allTogglesDisabledItems || !item.disabled)
                  .map(({value}) => value);
                const togglableValues = new Set(togglable);
                const newValues: string[] = allSelected
                  ? selectedData.filter((value) => !togglableValues.has(value))
                  : [
                      ...selectedData,
                      ...togglable.filter(
                        (value) => !selectedData.includes(value)
                      ),
                    ];
                onChange(newValues);
              })
            }
            indeterminate={!!selectedData.length && !allSelected}
            {...checkboxProps(allSelected)}
          />
        }
        {...formLabelProps(includeAllLabel || "All")}
      />
    </Box>
  );

  const renderItem = (index: number, style?: CSSProperties | undefined) => {
    const item = items[index];

    if (!item) return null;
    const {label, value, disabled} = item;
    const isSelected = selectedData.includes(value);

    return (
      <Box key={value} className={classNames?.itemContainer} style={style}>
        <Box className={clsx(classes.itemContainer, classNames?.item)}>
          <FormControlLabel
            key={value}
            control={
              <Checkbox
                disabled={disabled}
                name={value}
                onChange={
                  propsOnChange ??
                  ((e) => {
                    const {checked} = e.target;
                    const newValues: string[] = checked
                      ? [...selectedData, value]
                      : selectedData.filter((selected) => selected !== value);

                    onChange(newValues);
                  })
                }
                {...checkboxProps(isSelected)}
              />
            }
            {...formLabelProps(label)}
          />
          {renderAdditionalComponent &&
            isSelected &&
            renderAdditionalComponent(item)}
        </Box>
        {item?.children && item?.children(isSelected)}
      </Box>
    );
  };

  const renderCheckboxes = () => {
    const height = Math.min(400, items.length * 50);

    const itemHasNested = items.some(({children}) => children);

    if (itemHasNested || ignoreVirtualization) {
      return items.map((_, index) => {
        return renderItem(index);
      });
    }

    return (
      <List
        height={height}
        itemCount={items.length}
        itemSize={50}
        width="100%"
        onItemsRendered={({visibleStopIndex}) => {
          if (visibleStopIndex >= items.length - 1 - END_REACHED_OFFSET) {
            onEndReached?.();
          }
        }}
      >
        {({index, style}) => {
          return renderItem(index, style);
        }}
      </List>
    );
  };

  return (
    <Box className={classes.list} style={{maxHeight}}>
      {includeAll && renderAllCheckbox()}
      {renderCheckboxes()}
      {checkboxGroupError && (
        <FormHelperText error>{checkboxGroupError.message}</FormHelperText>
      )}
    </Box>
  );
};

export default CheckboxGroup;
